/**
 * Constantes de futebol e de calibração do motor.
 *
 * Os números de simulação de partida (BASE_*, CHANCE_EXP, CONV_EXP) foram
 * derivados para satisfazer a tabela de variância publicada em docs/SPEC.md,
 * seção 6.6. Mudar qualquer um deles exige rodar `pnpm test:stats`.
 */
export const ENGINE_VERSION = "0.1.0";
// ---------------------------------------------------------------------------
// Pesos de atributo por posição (docs/SPEC.md 3.2)
// ---------------------------------------------------------------------------
export const POSITION_WEIGHTS = {
    GOL: { ref: 35, col: 30, sai: 20, pen: 15 },
    ZAG: { mar: 25, des: 25, cab: 20, vel: 10, pas: 10, res: 10 },
    LD: { vel: 20, cru: 20, mar: 20, res: 15, des: 15, pas: 10 },
    LE: { vel: 20, cru: 20, mar: 20, res: 15, des: 15, pas: 10 },
    VOL: { des: 25, mar: 20, pas: 20, res: 20, arm: 10, cab: 5 },
    MC: { pas: 25, arm: 20, res: 20, des: 15, mar: 10, dri: 10 },
    MEI: { arm: 30, pas: 25, dri: 20, fin: 15, vel: 10 },
    PD: { vel: 25, dri: 25, cru: 20, fin: 15, res: 15 },
    PE: { vel: 25, dri: 25, cru: 20, fin: 15, res: 15 },
    ATA: { fin: 35, cab: 20, dri: 15, vel: 15, arm: 15 },
};
/** Distância entre posições. Usada para a penalidade de improviso. */
const POSITION_GROUP = {
    GOL: 0,
    ZAG: 1,
    LD: 2,
    LE: 2,
    VOL: 3,
    MC: 4,
    MEI: 5,
    PD: 6,
    PE: 6,
    ATA: 7,
};
/**
 * Multiplicador de força ao escalar um jogador fora da posição natural.
 *
 * A penalidade precisa doer. Com valores brandos, um zagueiro forte vira o
 * melhor lateral do elenco e a escalação automática monta times com meia
 * dúzia de improvisados, o que tira o sentido de ter profundidade em cada
 * posição. Trocar apenas o lado, de direita para esquerda, continua barato.
 */
export function positionPenalty(natural, playing) {
    if (natural === playing)
        return 1;
    const isKeeper = natural === "GOL" || playing === "GOL";
    if (isKeeper)
        return 0.4; // goleiro na linha ou jogador de linha no gol
    if ((natural === "LD" && playing === "LE") || (natural === "LE" && playing === "LD"))
        return 0.94;
    if ((natural === "PD" && playing === "PE") || (natural === "PE" && playing === "PD"))
        return 0.94;
    const distance = Math.abs(POSITION_GROUP[natural] - POSITION_GROUP[playing]);
    if (distance <= 1)
        return 0.86;
    if (distance === 2)
        return 0.76;
    if (distance === 3)
        return 0.68;
    return 0.6;
}
export const FORMATIONS = {
    "4-4-2": { name: "4-4-2", slots: ["GOL", "LD", "ZAG", "ZAG", "LE", "PD", "MC", "MC", "PE", "ATA", "ATA"] },
    "4-3-3": { name: "4-3-3", slots: ["GOL", "LD", "ZAG", "ZAG", "LE", "VOL", "MC", "MEI", "PD", "ATA", "PE"] },
    "4-2-3-1": { name: "4-2-3-1", slots: ["GOL", "LD", "ZAG", "ZAG", "LE", "VOL", "VOL", "PD", "MEI", "PE", "ATA"] },
    "3-5-2": { name: "3-5-2", slots: ["GOL", "ZAG", "ZAG", "ZAG", "PD", "VOL", "MC", "MEI", "PE", "ATA", "ATA"] },
    "5-3-2": { name: "5-3-2", slots: ["GOL", "LD", "ZAG", "ZAG", "ZAG", "LE", "VOL", "MC", "MEI", "ATA", "ATA"] },
    "4-4-1-1": { name: "4-4-1-1", slots: ["GOL", "LD", "ZAG", "ZAG", "LE", "PD", "MC", "VOL", "PE", "MEI", "ATA"] },
    // As vagas vêm linha por linha, da defesa ao ataque: é assim que a tela desenha
    // o campo, lendo os números do nome como tamanho de cada linha.
    "4-5-1": { name: "4-5-1", slots: ["GOL", "LD", "ZAG", "ZAG", "LE", "PD", "MC", "VOL", "MC", "PE", "ATA"] },
    "4-1-4-1": { name: "4-1-4-1", slots: ["GOL", "LD", "ZAG", "ZAG", "LE", "VOL", "PD", "MC", "MC", "PE", "ATA"] },
    "3-4-3": { name: "3-4-3", slots: ["GOL", "ZAG", "ZAG", "ZAG", "LD", "MC", "MC", "LE", "PD", "ATA", "PE"] },
    "5-4-1": { name: "5-4-1", slots: ["GOL", "LD", "ZAG", "ZAG", "ZAG", "LE", "PD", "MC", "MC", "PE", "ATA"] },
};
export const DEFAULT_FORMATION = "4-4-2";
// ---------------------------------------------------------------------------
// Pesos de setor: quanto cada posição contribui para defesa, meio e ataque
// ---------------------------------------------------------------------------
export const SECTOR_WEIGHTS = {
    GOL: { def: 1.0, mid: 0.0, att: 0.0 },
    ZAG: { def: 1.0, mid: 0.15, att: 0.05 },
    LD: { def: 0.6, mid: 0.4, att: 0.15 },
    LE: { def: 0.6, mid: 0.4, att: 0.15 },
    VOL: { def: 0.5, mid: 0.8, att: 0.1 },
    MC: { def: 0.25, mid: 1.0, att: 0.25 },
    MEI: { def: 0.1, mid: 0.9, att: 0.6 },
    PD: { def: 0.1, mid: 0.4, att: 0.8 },
    PE: { def: 0.1, mid: 0.4, att: 0.8 },
    ATA: { def: 0.02, mid: 0.2, att: 1.0 },
};
// ---------------------------------------------------------------------------
// Calibração da partida (docs/SPEC.md 6)
// ---------------------------------------------------------------------------
/** Segmentos de 5 minutos em uma partida de 90. */
export const SEGMENTS = 18;
/** Probabilidade de criar chance em um segmento com a posse, em forças iguais. */
export const BASE_CHANCE = 0.4;
/** Probabilidade de converter uma chance em gol, em forças iguais. */
export const BASE_CONVERSION = 0.36;
/**
 * Expoentes da razão ataque/defesa. A soma (1.18) é o que governa a curva de
 * favoritismo da tabela de SPEC 6.6. A divisão entre criar e converter muda a
 * textura do jogo (mais chances ou mais eficiência) sem mexer no resultado.
 */
export const CHANCE_EXP = 0.5;
export const CONVERSION_EXP = 0.68;
/** Trilhos de segurança. Só prendem em confrontos muito desequilibrados. */
export const CHANCE_MIN = 0.06;
export const CHANCE_MAX = 0.6;
export const CONVERSION_MIN = 0.08;
export const CONVERSION_MAX = 0.6;
/**
 * Vantagem de jogar em casa, aplicada a todos os setores. Vale cerca de 15
 * pontos percentuais de diferença entre vitórias em casa e fora, que é o que
 * se observa nas ligas nacionais reais.
 */
export const HOME_ADVANTAGE = 1.08;
export const STYLE_MODIFIERS = {
    defensivo: { att: 0.9, def: 1.1 },
    equilibrado: { att: 1.0, def: 1.0 },
    ofensivo: { att: 1.1, def: 0.9 },
};
export const MARKING_MODIFIERS = {
    leve: { def: 0.95, fatigue: 0.85, card: 0.6 },
    normal: { def: 1.0, fatigue: 1.0, card: 1.0 },
    forte: { def: 1.08, fatigue: 1.25, card: 1.8 },
};
export const TEMPO_MODIFIERS = {
    baixo: { first: 0.96, secondPenalty: 0.0, fatigue: 0.85 },
    normal: { first: 1.0, secondPenalty: 0.0, fatigue: 1.0 },
    alto: { first: 1.05, secondPenalty: 0.08, fatigue: 1.3 },
};
// ---------------------------------------------------------------------------
// Condição, lesão e moral
// ---------------------------------------------------------------------------
export const CONDITION_LOSS_BASE = 13;
export const CONDITION_RECOVERY_BASE = 25;
/**
 * Fração da recuperação que quem entrou em campo recebe até a próxima rodada.
 *
 * Calibrado para que jogar toda rodada custe de 2 a 5 pontos de condição, na
 * conta da Resistência do jogador e do nível do centro de treinamento. Com isso
 * um titular resistente em clube estruturado atravessa a temporada, e um
 * jogador frágil em clube pequeno precisa ser poupado a cada oito jogos.
 *
 * O valor anterior, 0.45, deixava a recuperação maior que o desgaste quando o
 * centro de treinamento era nível 5, e a condição ficava sempre em 100.
 */
export const PLAYED_RECOVERY_FACTOR = 0.33;
/** Condição abaixo deste valor começa a derrubar a força efetiva. */
export const CONDITION_THRESHOLD = 60;
export const INJURY_CHANCE_PER_SEGMENT = 0.0016;
export const MORALE_LOW = 30;
// ---------------------------------------------------------------------------
// Evolução por idade (docs/SPEC.md 3.3)
// ---------------------------------------------------------------------------
/** Ganho ou perda base de atributo por temporada, por idade. */
export function ageDelta(age) {
    if (age <= 18)
        return 4.2;
    if (age <= 21)
        return 3.4;
    if (age <= 23)
        return 2.4;
    if (age <= 25)
        return 1.4;
    if (age <= 27)
        return 0.6;
    if (age <= 29)
        return 0.0;
    if (age <= 31)
        return -1.2;
    if (age <= 33)
        return -2.6;
    if (age <= 35)
        return -4.2;
    return -6.0;
}
export const RETIREMENT_MIN_AGE = 33;
// ---------------------------------------------------------------------------
// Estruturas
// ---------------------------------------------------------------------------
/**
 * Nome de cada estrutura, como chave de rótulo.
 *
 * Era uma tabela em português que a tela do clube lia direto, e por isso o
 * "Centro de treinamento" aparecia assim para quem tinha escolhido inglês.
 */
export const FACILITY_LABEL_KEYS = {
    stadium: "estrutura.stadium",
    youth: "estrutura.youth",
    training: "estrutura.training",
    medical: "estrutura.medical",
};
/** Custo de obra para chegar ao nível indicado. */
export const FACILITY_COST = {
    1: 0,
    2: 1_500_000,
    3: 5_000_000,
    4: 14_000_000,
    5: 34_000_000,
};
/** Rodadas de obra até o nível indicado. */
export const FACILITY_ROUNDS = { 1: 0, 2: 4, 3: 6, 4: 9, 5: 12 };
/** Manutenção por rodada, por nível. */
export const FACILITY_UPKEEP = {
    1: 8_000,
    2: 25_000,
    3: 55_000,
    4: 120_000,
    5: 250_000,
};
/** Multiplicador de renda e de público pelo conforto do estádio. */
export const STADIUM_COMFORT = {
    1: 0.88,
    2: 0.95,
    3: 1.0,
    4: 1.07,
    5: 1.15,
};
// ---------------------------------------------------------------------------
// Finanças
// ---------------------------------------------------------------------------
/** Receita de TV por rodada, por divisão. */
export const TV_BY_TIER = { 1: 700_000, 2: 240_000, 3: 80_000, 4: 25_000 };
/** Patrocínio por rodada, por reputação. */
export const SPONSOR_BY_REPUTATION = {
    1: 25_000,
    2: 70_000,
    3: 190_000,
    4: 500_000,
    5: 1_100_000,
    6: 2_000_000,
};
/** Fração da torcida que comparece quando o estádio comporta. */
export const ATTENDANCE_RATE = 0.06;
/** Receita de sócios por torcedor, por rodada. */
export const MEMBER_REVENUE_PER_FAN = 0.4;
export const LOAN_MAX_MULTIPLIER = 3;
export const LOAN_RATE_MIN = 0.02;
export const LOAN_RATE_MAX = 0.05;
/** Rodadas de caixa negativo antes da diretoria intervir. */
export const BANKRUPTCY_GRACE = 6;
// ---------------------------------------------------------------------------
// Elenco
// ---------------------------------------------------------------------------
export const SQUAD_MIN = 18;
export const SQUAD_MAX = 32;
export const STAR_THRESHOLD = 85;
export const MAX_STARS_WITHOUT_LEADER = 4;
export const SHIRT_NUMBERS_MAX = 45;
//# sourceMappingURL=constants.js.map